import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n, LOGO_TRANSPARENT } from "@/lib/i18n";

const NAV = [
  { id: "about", key: "about" },
  { id: "services", key: "services" },
  { id: "experience", key: "experience" },
  { id: "journal", key: "instagram" },
  { id: "contact", key: "contact" },
];

const LANGS = [
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "fr", label: "FR" },
];

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        scrolled
          ? "bg-ink-900/85 backdrop-blur-xl border-b border-sand-50/10 py-3"
          : "bg-transparent py-4 md:py-5"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3"
          data-testid="logo-button"
          aria-label="Villa Reset Ibiza — Home"
        >
          <img
            src={LOGO_TRANSPARENT}
            alt="Villa Reset Ibiza"
            className={`transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)] select-none pointer-events-none ${
              scrolled ? "h-10 md:h-11" : "h-12 md:h-14"
            }`}
            draggable={false}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-12">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              className="group relative text-[10.5px] uppercase tracking-[0.32em] py-1 text-sand-50/70 hover:text-gold transition-colors duration-500"
              data-testid={`nav-${n.id}-button`}
            >
              <span className="relative">
                {t.nav[n.key]}
                <span
                  className="absolute -bottom-1 left-0 right-0 mx-auto h-px w-0 bg-gold transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:w-full"
                  aria-hidden="true"
                />
              </span>
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden sm:flex items-center gap-1 border border-sand-50/20 px-2 py-1"
            role="group"
            aria-label="Language"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`lang-${l.code}-button`}
                className={`text-[10px] tracking-[0.25em] px-2 py-1 transition-colors ${
                  lang === l.code
                    ? "bg-gold text-ink-900"
                    : "text-sand-50/70 hover:text-sand-50"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollTo("contact")}
            className="hidden md:inline-flex items-center justify-center border border-gold/70 text-gold px-6 py-3 text-[11px] uppercase tracking-[0.3em] hover:bg-gold hover:text-ink-900 transition-colors"
            data-testid="header-book-button"
          >
            {t.nav.request}
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-sand-50"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} strokeWidth={1.4} /> : <Menu size={20} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden bg-ink-900 border-t border-sand-50/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => scrollTo(n.id)}
                  className="text-left text-sm uppercase tracking-[0.3em] text-sand-50"
                  data-testid={`mobile-nav-${n.id}-button`}
                >
                  {t.nav[n.key]}
                </button>
              ))}
              <div className="flex items-center gap-2 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`mobile-lang-${l.code}-button`}
                    className={`text-[10px] tracking-[0.3em] px-3 py-2 border ${
                      lang === l.code
                        ? "bg-gold text-ink-900 border-gold"
                        : "border-sand-50/20 text-sand-50"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => scrollTo("contact")}
                className="mt-2 inline-flex items-center justify-center border border-gold text-gold px-6 py-4 text-[11px] uppercase tracking-[0.3em]"
                data-testid="mobile-book-button"
              >
                {t.nav.request}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
